import { useQueryClient } from '@tanstack/react-query'
import { CircleCheck, PlayCircle } from 'lucide-react'
import { useId, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'
import { formatDate } from '@/lib/formatters'
import { cn } from '@/lib/utils'
import { CacheKeys } from '../../../../../lib/cache'
import { parseError } from '../../../../../lib/errors'
import { coiDocumentDownloadHref } from '../../../../../models/coi'
import {
  type ChecklistEntry,
  ChecklistItem,
  setChecklistItemCompleted,
  usePostBindChecklist,
} from '../../../../../models/insurancePolicy'

/** The team's walkthrough of what to check on a COI before it goes out. */
const COI_REVIEW_VIDEO_URL = 'https://drive.google.com/file/d/1uyvH8R18egrVKP7NCqGFEtAJr9wV4g9h/view'

// The warning banner's amber, as a strip across the top of the policy card.
const BOX_CLASS = 'border-b border-(--amber)/40 bg-(--amber)/15 px-6 py-4'

const ITEM_TITLES: Record<ChecklistItem, string> = {
  [ChecklistItem.POLICY]: 'Policy',
  [ChecklistItem.COI]: 'COI',
  [ChecklistItem.WELCOME_PACKAGE]: 'Welcome package',
}

function itemDescription(entry: ChecklistEntry): string {
  switch (entry.item) {
    case ChecklistItem.POLICY:
      return 'Completes on its own once the policy document is uploaded.'
    case ChecklistItem.COI:
      return entry.canToggle
        ? 'Review the COI document: it’s valid and everything looks good.'
        : 'Opens once this policy has a COI.'
    case ChecklistItem.WELCOME_PACKAGE:
      return 'Send the customer their welcome package.'
  }
}

/**
 * The new policy checklist at the top of a policy card in the internal Insurance tab. Cai admins only.
 * Once every item is done it collapses to a one-line "complete" note.
 */
export function PostBindChecklist({ orgId, policyId }: { orgId: string; policyId: string }) {
  const { checklist, isLoading } = usePostBindChecklist(orgId, policyId)
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)
  // A finished checklist stays collapsed unless staff open it, e.g. to undo a tick.
  const [isExpanded, setIsExpanded] = useState(false)
  const idPrefix = useId()

  async function onCheckedChange(item: ChecklistItem, completed: boolean) {
    setSaving(true)
    try {
      const updated = await setChecklistItemCompleted(orgId, policyId, item, completed)
      queryClient.setQueryData([CacheKeys.POST_BIND_CHECKLIST, policyId], updated)
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className={BOX_CLASS}>
        <SkeletonRows rows={3} />
      </div>
    )
  }
  if (!checklist) return null

  const doneCount = checklist.entries.filter((entry) => entry.isCompleted).length
  const isComplete = doneCount === checklist.entries.length

  if (isComplete && !isExpanded) {
    return (
      <div className={cn(BOX_CLASS, 'flex items-center justify-between gap-2 py-3')}>
        <span className="text-md flex items-center gap-2 font-semibold text-(--amber-text)">
          <CircleCheck className="size-4" />
          New policy checklist complete
        </span>
        <Button variant="link" size="sm" className="h-auto px-0" onClick={() => setIsExpanded(true)}>
          Show
        </Button>
      </div>
    )
  }

  return (
    <div className={cn(BOX_CLASS, 'flex flex-col gap-3')}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-md font-semibold text-(--amber-text)">New policy checklist</span>
        {isComplete ? (
          <Button variant="link" size="sm" className="h-auto px-0" onClick={() => setIsExpanded(false)}>
            Hide
          </Button>
        ) : (
          <span className="text-muted-foreground text-sm">
            {doneCount} of {checklist.entries.length} done
          </span>
        )}
      </div>
      <ul className="flex flex-col gap-4">
        {checklist.entries.map((entry) => {
          const checkboxId = `${idPrefix}-${entry.item}`
          return (
            <li key={entry.item} className="flex items-start gap-3">
              <Checkbox
                id={checkboxId}
                className="mt-1"
                checked={entry.isCompleted}
                disabled={!entry.canToggle || saving}
                onCheckedChange={(checked) => onCheckedChange(entry.item, checked === true)}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <Label htmlFor={checkboxId} className="md:text-md text-black">
                  {ITEM_TITLES[entry.item]}
                </Label>
                <span className="text-muted-foreground text-sm">{itemDescription(entry)}</span>
                {entry.item === ChecklistItem.COI && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto w-fit px-0 has-[>svg]:px-0"
                    href={COI_REVIEW_VIDEO_URL}
                    target="_blank"
                  >
                    <PlayCircle />
                    Watch how to review a COI
                  </Button>
                )}
                {entry.isCompleted && entry.dateCompleted && (
                  <span className="text-muted-foreground text-sm">
                    Done {formatDate(entry.dateCompleted)}
                    {entry.completedByName && ` by ${entry.completedByName}`}
                  </span>
                )}
              </div>
              {entry.item === ChecklistItem.COI && checklist.coiDocumentId && (
                <Button
                  variant="link"
                  size="sm"
                  href={coiDocumentDownloadHref(checklist.coiDocumentId, orgId)}
                  target="_blank"
                >
                  View COI
                </Button>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
